import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users, CheckCircle, XCircle, Clock, PauseCircle, ArrowClockwise,
  MagnifyingGlass, EnvelopeSimple, WarningCircle,
} from '@phosphor-icons/react';
import { API } from '@/config/api';

/* --------------------------------------------------------------------------
 * Admin — Sub-Agent Approvals panel.
 * Split from Dashboard.js to keep that file from growing further.
 * ------------------------------------------------------------------------- */
const STATUS_META = {
  pending:   { label: 'Pending approval', tone: 'bg-amber-50 text-amber-800 border-amber-200',       Icon: Clock },
  active:    { label: 'Active',           tone: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: CheckCircle },
  suspended: { label: 'Suspended',        tone: 'bg-rose-50 text-rose-700 border-rose-200',          Icon: PauseCircle },
  rejected:  { label: 'Rejected',         tone: 'bg-gray-100 text-gray-600 border-gray-200',         Icon: XCircle },
};

const FILTERS = [
  { id: 'pending',   label: 'Pending' },
  { id: 'active',    label: 'Active' },
  { id: 'suspended', label: 'Suspended' },
  { id: 'rejected',  label: 'Rejected' },
  { id: 'all',       label: 'All' },
];

export default function AdminSubAgentsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sub-agents`);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load sub-agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, active: 0, suspended: 0, rejected: 0 };
    rows.forEach(r => { c[r.sub_agent_status] = (c[r.sub_agent_status] || 0) + 1; });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter !== 'all' && r.sub_agent_status !== filter) return false;
      if (!q) return true;
      return (r.name || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q)
        || (r.parent_recruiter_name || '').toLowerCase().includes(q);
    });
  }, [rows, filter, search]);

  const doAction = async (row, action) => {
    setBusyId(row.id);
    try {
      await axios.post(`${API}/admin/sub-agents/${row.id}/${action}`);
      toast.success(
        action === 'approve' ? 'Sub-agent approved — they can log in now'
        : action === 'reject' ? 'Sub-agent rejected'
        : action === 'suspend' ? 'Sub-agent suspended'
        : 'Sub-agent reactivated'
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-sub-agents-panel">
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 lg:px-6 py-5 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center justify-center">
                <Users size={17} weight="fill" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900" style={{ fontFamily: "'Libre Baskerville', serif" }}>Sub-Agent Approvals</h2>
              {counts.pending > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-bold text-white bg-amber-500 rounded-full" data-testid="pending-count-badge">
                  {counts.pending}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Recruiters can invite team members to work under their account. Each new sub-agent needs your approval before they can log in.
            </p>
          </div>
          <div className="relative w-full lg:w-64">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search name, email, parent…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 bg-gray-50 border-gray-200"
              data-testid="sa-search-input"
            />
          </div>
        </div>

        {/* Filter pills */}
        <div className="px-5 lg:px-6 py-3 border-b border-gray-100 flex flex-wrap gap-1.5" data-testid="sa-filter-pills">
          {FILTERS.map(f => {
            const active = filter === f.id;
            const count = counts[f.id] ?? 0;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold rounded-full border transition-colors whitespace-nowrap ${active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                data-testid={`sa-filter-${f.id}`}
              >
                {f.label}
                <span className={`inline-flex items-center justify-center min-w-[18px] px-1 text-[10px] font-bold rounded-full ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
              <Users size={22} className="text-gray-300" />
            </div>
            <p className="text-sm font-medium text-gray-600">
              {filter === 'pending' ? 'No sub-agents awaiting approval.' : 'No sub-agents match this filter.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/70 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Sub-Agent</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Parent Recruiter</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Status</th>
                  <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em] hidden md:table-cell">Invited</th>
                  <th className="text-right px-5 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-[0.08em]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(row => {
                  const meta = STATUS_META[row.sub_agent_status] || STATUS_META.pending;
                  const StatusIcon = meta.Icon;
                  const isBusy = busyId === row.id;
                  return (
                    <tr key={row.id} className="hover:bg-blue-50/40 transition-colors" data-testid={`admin-sa-row-${row.id}`}>
                      <td className="px-5 py-3">
                        <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                        <p className="text-[11px] text-gray-500 flex items-center gap-1"><EnvelopeSimple size={10} /> {row.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {row.parent_recruiter_name ? (
                          <>
                            <p className="text-sm text-gray-800">{row.parent_recruiter_name}</p>
                            <p className="text-[11px] text-gray-500">{row.parent_recruiter_email}</p>
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-rose-600 font-semibold">
                            <WarningCircle size={12} weight="fill" /> Parent missing
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-full border ${meta.tone}`} data-testid={`admin-sa-status-${row.id}`}>
                          <StatusIcon size={12} weight="fill" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-gray-500 hidden md:table-cell whitespace-nowrap">
                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {row.sub_agent_status === 'pending' && (
                            <>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs" disabled={isBusy} onClick={() => doAction(row, 'approve')} data-testid={`admin-sa-approve-${row.id}`}>
                                <CheckCircle size={14} className="mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-600 hover:bg-rose-50" disabled={isBusy} onClick={() => { if (window.confirm(`Reject "${row.name}"? This cannot be undone.`)) doAction(row, 'reject'); }} data-testid={`admin-sa-reject-${row.id}`}>
                                <XCircle size={14} className="mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {row.sub_agent_status === 'active' && (
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-rose-600 hover:bg-rose-50" disabled={isBusy} onClick={() => doAction(row, 'suspend')} data-testid={`admin-sa-suspend-${row.id}`}>
                              <PauseCircle size={14} className="mr-1" /> Suspend
                            </Button>
                          )}
                          {row.sub_agent_status === 'suspended' && (
                            <Button size="sm" variant="ghost" className="h-8 text-xs text-emerald-700 hover:bg-emerald-50" disabled={isBusy} onClick={() => doAction(row, 'reactivate')} data-testid={`admin-sa-reactivate-${row.id}`}>
                              <ArrowClockwise size={14} className="mr-1" /> Reactivate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
